// src/utilities/tokenUtils.js
import { toast } from 'react-toastify';
import * as impersonation from './impersonation.js';
import { decryptData, encryptData } from './encryption';

export const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        return decodedToken.exp < currentTime;
    } catch {
        return true;
    }
};

export const getTokenFromLocalStorage = () => {
    return localStorage.getItem('token');
};

/**
 * Persist a rotated token pair without changing the profile's storage format.
 * Profiles are AES-encrypted by the AUTH reducer; treating that ciphertext as
 * JSON during a refresh makes an otherwise valid session unrecoverable.
 */
export const persistRefreshedSession = ({ accessToken, refreshToken }) => {
    if (!accessToken) throw new Error('Refresh response did not include an access token');

    const encryptedProfile = localStorage.getItem('profile');
    if (!encryptedProfile) throw new Error('No stored profile to refresh');

    const profile = decryptData(encryptedProfile);
    if (!profile || typeof profile !== 'object') {
        throw new Error('Stored profile is invalid');
    }

    const refreshedProfile = {
        ...profile,
        token: accessToken,
        ...(refreshToken ? { refreshToken } : {}),
    };

    localStorage.setItem('profile', encryptData(refreshedProfile));
    localStorage.setItem('token', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

    return refreshedProfile;
};

let _sessionExpiredFired = false;

/**
 * Production-grade session expiry handler.
 * - Shows a user-friendly toast (fires only once per page load)
 * - Dispatches LOGOUT through the Redux store so all reducers clean up
 * - Redirects to the login page
 *
 * Safe to call from anywhere (axios interceptors, utility functions, components).
 */
export const sessionExpired = (reason = 'expired') => {
    if (_sessionExpiredFired) return;
    _sessionExpiredFired = true;

    // A vendor session a super user opened ("Sign in as vendor") has a hard
    // 60-minute life and no refresh token, so it ends up here on its own.
    // The right outcome is the admin's own session back, not a login page:
    // the admin never signed out.
    if (impersonation.active() && impersonation.end()) {
        toast.info('The vendor session has ended. You are back on your admin account.', {
            position: 'top-center',
            autoClose: 4000,
        });
        setTimeout(() => {
            _sessionExpiredFired = false;
            window.location.replace(impersonation.RETURN_ROUTE);
        }, 800);
        return;
    }

    // Clear storage immediately so nothing stale persists
    localStorage.removeItem('profile');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');

    // Fire the toast before the redirect so the user sees it briefly
    const messages = {
        expired: 'Your session has expired. Please log in again.',
        invalid: 'Your session is no longer valid. Please log in again.',
        forced:  'You have been signed out. Please log in to continue.',
    };
    toast.warn(messages[reason] || messages.expired, {
        toastId:       'session-expired',
        autoClose:     4000,
        closeOnClick:  true,
        pauseOnHover:  false,
    });

    // Dispatch LOGOUT through the Redux store so every reducer resets
    try {
        // Lazy-require to avoid circular imports at module load time
        const store = require('../reducers/store').default;
        store.dispatch({ type: 'LOGOUT' });
    } catch (e) {
        // store not yet initialised (e.g. called during very early boot)
        console.warn('[sessionExpired] could not dispatch LOGOUT:', e.message);
    }

    // Brief delay lets the toast render before the page unloads
    setTimeout(() => {
        _sessionExpiredFired = false; // reset for next login within the same tab
        if (window.location.pathname !== '/auth/login-page') {
            window.location.replace('/auth/login-page');
        }
    }, 1500);
};
